import type { MarketingPageKey } from '@/components/marketing/links'

/**
 * Blog post registry — the single source of truth for the /blog section.
 *
 * All prose lives in i18n under `blog.<i18nKey>.*` (en.json / zh.json) so every
 * article is bilingual; this file only carries *structure*: the URL slug, the
 * i18n namespace, a fixed publish date (never Date.now() under the prerender),
 * tags, the ordered list of prose section names to render, and the cross-link
 * graph. `relatedKeys` may point at marketing pages (their MarketingPageKey) or
 * at other posts (their slug) — `buildBlogRelatedLinks` resolves both.
 */
export interface BlogPost {
  /** URL segment: /blog/<slug>. Also used as a related-link reference. */
  readonly slug: string
  /** i18n namespace under `blog.*`. */
  readonly i18nKey: string
  /** Fixed ISO publish date. */
  readonly date: string
  /** Free-form topic tags shown on cards and the article header. */
  readonly tags: readonly string[]
  /**
   * Ordered prose section names. Each resolves to `blog.<i18nKey>.<name>` with
   * a `.title` (H2) and `.body` (string[] of paragraphs) — rendered generically
   * by the post page, so a new article is just copy plus an entry here.
   */
  readonly sections: readonly string[]
  /** Cross-links: marketing page keys and/or other post slugs, in order. */
  readonly relatedKeys: readonly (MarketingPageKey | string)[]
}

const DATE = '2026-06-12'

export const BLOG_POSTS: readonly BlogPost[] = [
  {
    slug: 'how-to-research-any-topic-fast-with-ai',
    i18nKey: 'researchFastWithAi',
    date: DATE,
    tags: ['AI research', 'How-to', 'Workflow'],
    sections: ['why', 'steps', 'citations', 'whenGraph', 'mistakes'],
    relatedKeys: [
      'how-to-fact-check-ai-answers-with-sources',
      'whatIsKnowledgeGraph',
      'useCasesResearchers',
    ],
  },
  {
    slug: 'how-to-fact-check-ai-answers-with-sources',
    i18nKey: 'factCheckAiAnswers',
    date: DATE,
    tags: ['Fact-checking', 'Citations', 'AI research'],
    sections: ['why', 'steps', 'redFlags', 'tooling', 'workflow'],
    relatedKeys: [
      'how-to-avoid-ai-hallucinations-with-cited-answers',
      'how-to-research-any-topic-fast-with-ai',
      'comparePerplexity',
    ],
  },
  {
    slug: 'how-to-avoid-ai-hallucinations-with-cited-answers',
    i18nKey: 'avoidHallucinations',
    date: DATE,
    tags: ['AI hallucinations', 'Citations', 'Trust'],
    sections: ['whyHallucinate', 'whatAreCited', 'howToAvoid', 'limits', 'mindweb'],
    relatedKeys: [
      'how-to-fact-check-ai-answers-with-sources',
      'best-deep-research-tools-2026',
      'whatIsKnowledgeGraph',
    ],
  },
  {
    slug: 'best-deep-research-tools-2026',
    i18nKey: 'bestDeepResearchTools',
    date: DATE,
    tags: ['Tools', 'Comparison', 'Deep research'],
    sections: ['intro', 'whatMakesGood', 'tools', 'howToChoose'],
    relatedKeys: [
      'how-to-research-any-topic-fast-with-ai',
      'comparePerplexity',
      'compareNotebooklm',
    ],
  },
] as const

/** Look up a post by slug. Returns undefined for unknown slugs. */
export function findBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((post) => post.slug === slug)
}
